import {
  Flex,
  Spinner,
  Text,
  Em,
  Separator,
  TextField,
} from "@radix-ui/themes";
import Switch from "@/components/shared/inputs/switch";
import { useQuackchatSessionConfiguration } from "@/api/quackchat/configuration/useQuackchatSessionConfiguration";
import { AutomationConfigurationContentHeader } from "../parts/AutomationConfigurationContentHeader";
import { Link, useParams } from "react-router-dom";
import { useReleases } from "@/routes/simulator/context/release";
import { toast } from "sonner";
import { ConfigurationViewGuard } from "../parts/ConfigurationViewGuard";
import { Skill } from "@/v2/entities/skill/Skill.model";
import { useGetSkills } from "@/v2/entities/skill/useGetSkills";
import {
  useGetSkillsForRelease,
  useReleaseApi,
} from "@/v2/entities/release/Release.api";
import { useMutation } from "react-query";
import { useCallback, useMemo, useState } from "react";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";

type SkillPayload = Record<string, boolean>;

interface SkillItemProps {
  skill: Skill;
  enabled?: boolean;
  toggleEnabled: () => void;
  maxUsage?: number;
  onMaxUsageChange: (value: number | undefined) => void;
  showMaxUsageInput?: boolean;
}

function SkillItem({
  skill,
  enabled = false,
  toggleEnabled,
  maxUsage,
  onMaxUsageChange,
  showMaxUsageInput = false,
}: SkillItemProps) {
  const handleMaxUsageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === "") {
        onMaxUsageChange(undefined);
        return;
      }

      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        const clampedValue = Math.max(0, Math.min(10, numValue));
        onMaxUsageChange(clampedValue);
      }
    },
    [onMaxUsageChange]
  );

  return (
    <Flex justify={"between"} gap={"3"} align={"center"}>
      <Flex gap={"2"} direction={"column"}>
        <Text size={"2"} as={"label"}>
          {skill.name}
        </Text>

        <Text size={"2"} color={"gray"}>
          <Em>{skill.description}</Em>
        </Text>
      </Flex>

      <Flex flexShrink={"0"} gap={"2"} align={"center"}>
        {enabled && showMaxUsageInput && (
          <TextField.Root
            type="number"
            min={0}
            max={10}
            step={1}
            value={maxUsage ?? ""}
            onChange={handleMaxUsageChange}
            size="2"
            style={{ width: "60px" }}
            placeholder="0"
          />
        )}
        <Switch value={enabled} onChange={toggleEnabled} />
      </Flex>
    </Flex>
  );
}

interface SkillListProps {
  skills: Skill[];
  payload: SkillPayload;
  toggleSkill: (skill: Skill) => void;
  maxUsageMap: Record<string, number | undefined>;
  onMaxUsageChange: (skillId: string, value: number | undefined) => void;
  showMaxUsageInput?: boolean;
}

function SkillList({
  skills,
  payload,
  toggleSkill,
  maxUsageMap,
  onMaxUsageChange,
  showMaxUsageInput = false,
}: SkillListProps) {
  return (
    <Flex direction="column" gap="4">
      {skills.map((skill, index) => (
        <>
          <SkillItem
            key={skill.name}
            skill={skill}
            enabled={payload?.[skill.id]}
            toggleEnabled={() => toggleSkill(skill)}
            maxUsage={maxUsageMap[skill.id]}
            onMaxUsageChange={(value) => onMaxUsageChange(skill.id, value)}
            showMaxUsageInput={showMaxUsageInput}
          />

          {index < skills.length - 1 && (
            <Separator orientation="horizontal" size="4" />
          )}
        </>
      ))}
    </Flex>
  );
}
export function QuackchatSkills({ simulatorId }: { simulatorId?: string }) {
  const {
    data: configuration,
    isLoading,
    updateStatus,
  } = useQuackchatSessionConfiguration(simulatorId);

  const { releaseId, simulator } = useReleases();

  const releaseApi = useReleaseApi();

  const showMaxUsageInput = useFlag("skillMaxUsageInput", false);

  const { data: skillsForRelease, refetch: refetchSkillsForRelease } =
    useGetSkillsForRelease(releaseId);

  const [maxUsageMap, setMaxUsageMap] = useState<
    Record<string, number | undefined>
  >({});

  const { mutateAsync: addSkillToRelease } = useMutation({
    mutationKey: ["addSkillToRelease"],
    mutationFn: ({
      skillId,
      maxUsage,
    }: {
      skillId: string;
      maxUsage?: number;
    }) =>
      releaseApi.upsertSkillToRelease(releaseId?.toString() ?? "", {
        skillId,
        maxUsage,
      }),
    onSuccess: () => {
      refetchSkillsForRelease();
      toast("Skill added to release");
    },
  });

  const { mutateAsync: updateSkillMaxUsage } = useMutation({
    mutationKey: ["updateSkillMaxUsage"],
    mutationFn: ({
      skillId,
      maxUsage,
    }: {
      skillId: string;
      maxUsage?: number;
    }) =>
      releaseApi.upsertSkillToRelease(releaseId?.toString() ?? "", {
        skillId,
        maxUsage,
      }),
    onSuccess: () => {
      refetchSkillsForRelease();
    },
  });

  const { mutateAsync: removeSkillFromRelease } = useMutation({
    mutationKey: ["removeSkillFromRelease"],
    mutationFn: (skillId: string) =>
      releaseApi.removeSkillFromRelease(releaseId?.toString() ?? "", {
        skillId,
      }),
    onSuccess: (_, skillId) => {
      refetchSkillsForRelease();
      setMaxUsageMap((prev) => {
        const next = { ...prev };
        delete next[skillId];
        return next;
      });
      toast("Skill removed from release");
    },
  });

  const { data: skills, isLoading: isLoadingSkills } = useGetSkills();

  const skillPayload = useMemo<SkillPayload>(() => {
    return skillsForRelease?.skills
      ? skillsForRelease.skills.reduce(
          (acc, skill) => ({
            ...acc,
            [skill.id]: true,
          }),
          {}
        )
      : {};
  }, [skillsForRelease]);

  const updateMaxUsageCallback = useCallback(
    (skillId: string, maxUsage: number | undefined) => {
      updateSkillMaxUsage({ skillId, maxUsage });
    },
    [updateSkillMaxUsage]
  );

  const debouncedUpdateMaxUsage = useDebouncedCallback(
    updateMaxUsageCallback,
    500
  );

  const handleMaxUsageChange = useCallback(
    (skillId: string, value: number | undefined) => {
      setMaxUsageMap((prev) => ({
        ...prev,
        [skillId]: value,
      }));
      debouncedUpdateMaxUsage(skillId, value);
    },
    [debouncedUpdateMaxUsage]
  );

  const toggleSkill = useCallback(
    (skill: Skill) => {
      const isCurrentlyEnabled = skillPayload?.[skill.id] ?? false;

      if (isCurrentlyEnabled) {
        removeSkillFromRelease(skill.id);
      } else {
        const currentMaxUsage = maxUsageMap[skill.id];
        addSkillToRelease({ skillId: skill.id, maxUsage: currentMaxUsage });
      }
    },
    [skillPayload, removeSkillFromRelease, addSkillToRelease, maxUsageMap]
  );

  if (isLoadingSkills || !skills) {
    return <Spinner />;
  }

  if (
    simulator?.channel === "autonomous_quackchat" &&
    (isLoading || configuration == null)
  ) {
    return <Spinner />;
  }

  return (
    <Flex gap={"24px"}>
      <Flex flexGrow={"1"} direction={"column"} gap={"24px"}>
        <AutomationConfigurationContentHeader
          title={"Skills"}
          status={updateStatus}
        />

        <Flex direction={"column"} gap={"4"}>
          {skills.length > 0 ? (
            <SkillList
              skills={skills}
              payload={skillPayload}
              toggleSkill={toggleSkill}
              maxUsageMap={maxUsageMap}
              onMaxUsageChange={handleMaxUsageChange}
              showMaxUsageInput={showMaxUsageInput}
            />
          ) : (
            <Flex p="4">
              <Text size={"2"} color={"gray"}>
                No skills configured yet . Visit the{" "}
                <Link
                  className="text-blue-500 hover:text-blue-700"
                  to="/skills"
                >
                  skills page
                </Link>{" "}
                to configure skills.
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}

export function QuackchatSkillsConfiguration() {
  const { simulatorId } = useParams();

  return (
    <ConfigurationViewGuard view={"skills"}>
      <QuackchatSkills simulatorId={simulatorId} />
    </ConfigurationViewGuard>
  );
}
